import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Workbook } from 'exceljs';
import * as fs from 'file-saver';
import { NgxSpinnerService } from 'ngx-spinner';
import { MessService } from '../mess.service';
import { ToastmessageService } from 'src/app/toastmessage.service';
import { ConfirmationMessageDialogComponent } from '../confirmation-message-dialog/confirmation-message-dialog.component';

export interface MessSummaryDialogData {
  buildingId?: number | string;
  currentDate?: string;
  totalAmountValue?: number;
  supplyBuildings?: any[];
  readOnly?: boolean;
}

@Component({
  
  selector: 'app-mess-summary-dialog',
  templateUrl: './mess-summary-dialog.component.html',
  styleUrls: ['./mess-summary-dialog.component.scss'],
})
export class MessSummaryDialogComponent implements OnInit {
  buildingId: number | string | null = null;
  currentDate = '';
  BuildingDetails: any[] = [];
  supplyBuildingTableExpanded = true;
  readOnly = false;
  /** Prevents stacking multiple exceed alerts while the user keeps typing. */
  private studentStrengthAlertOpen = false;

  constructor(
    private dialogRef: MatDialogRef<MessSummaryDialogComponent>,
    private matDialog: MatDialog,
    private service: MessService,
    private spinner: NgxSpinnerService,
    private cdr: ChangeDetectorRef,
    private toast: ToastmessageService,
    @Inject(MAT_DIALOG_DATA) public data: MessSummaryDialogData,
  ) {}

  ngOnInit(): void {
    this.buildingId = this.data?.buildingId ?? null;
    this.currentDate = this.data?.currentDate ?? '';
    this.readOnly = !!this.data?.readOnly;

    if (Array.isArray(this.data?.supplyBuildings) && this.data.supplyBuildings.length > 0) {
      this.BuildingDetails = this.data.supplyBuildings.map((r: any) => this.normalizeRow(r));
      this.cdr.detectChanges();
      return;
    }

    this.getSupplyBuilding();
  }

  getSupplyBuilding(): void {
    if (!this.buildingId) {
      this.spinner.hide();
      this.cdr.detectChanges();
      return;
    }
    this.spinner.show();
    this.service.supplyBuildingDetails(this.buildingId, this.currentDate).subscribe({
      next: (res: any) => {
        if (!res || res == 204) {
          this.BuildingDetails = [];
          this.spinner.hide();
          this.cdr.detectChanges();
          return;
        }
        this.BuildingDetails = this.extractSupplyBuildingRows(res).map((r: any) =>
          this.normalizeRow(r),
        );
        this.spinner.hide();
        this.cdr.detectChanges();
      },
      error: () => {
        this.spinner.hide();
        this.cdr.detectChanges();
      },
    });
  }

  /** Supports `{ supplyBuildings: [...] }`, legacy array, or `{ data: [...] }`. */
  private extractSupplyBuildingRows(res: any): any[] {
    if (Array.isArray(res)) {
      return res;
    }
    if (Array.isArray(res?.supplyBuildings)) {
      return res.supplyBuildings;
    }
    if (Array.isArray(res?.data?.supplyBuildings)) {
      return res.data.supplyBuildings;
    }
    if (Array.isArray(res?.data)) {
      return res.data;
    }
    return [];
  }

  get totalCampus(): number {
    return this.BuildingDetails?.length ?? 0;
  }

  get totalHeadCount(): number {
    return (
      this.BuildingDetails?.reduce(
        (sum: number, r: any) =>
          sum +
          Number(r.studentCount ?? 0) +
          Number(r.employeeCount ?? 0) +
          Number(r.subStaffCount ?? 0) +
          Number(r.visitorsCount ?? 0),
        0,
      ) ?? 0
    );
  }

  get perHeadPrice(): number {
    const heads = Number(this.totalHeadCount ?? 0);
    const total = Number(this.data?.totalAmountValue ?? 0);
    if (!heads || heads <= 0) {
      return 0;
    }
    return Number((total / heads).toFixed(2));
  }

  get totalStudentStrength(): number {
    return (
      this.BuildingDetails?.reduce((s: number, r: any) => s + Number(r.scaitsStudentCount ?? 0), 0) ?? 0
    );
  }

  get totalStudents(): number {
    return this.BuildingDetails?.reduce((s: number, r: any) => s + Number(r.studentCount ?? 0), 0) ?? 0;
  }

  get totalEmployee(): number {
    return this.BuildingDetails?.reduce((s: number, r: any) => s + Number(r.employeeCount ?? 0), 0) ?? 0;
  }

  get totalSubStaff(): number {
    return this.BuildingDetails?.reduce((s: number, r: any) => s + Number(r.subStaffCount ?? 0), 0) ?? 0;
  }

  get totalVisitors(): number {
    return this.BuildingDetails?.reduce((s: number, r: any) => s + Number(r.visitorsCount ?? 0), 0) ?? 0;
  }

  inrGroupedNumber(value: any): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    const n = Number(String(value).replace(/,/g, ''));
    if (isNaN(n)) {
      return '';
    }
    return new Intl.NumberFormat('en-IN', {
      maximumFractionDigits: 2,
      minimumFractionDigits: 0,
    }).format(n);
  }

  // toggleSupplyBuildingTable(): void {
  //   this.supplyBuildingTableExpanded = !this.supplyBuildingTableExpanded;
  // }

  onCountChange(): void {
    // Whole numbers only — truncate any decimal that arrives via paste/autofill.
    for (const r of this.BuildingDetails ?? []) {
      for (const field of ['studentCount', 'employeeCount', 'subStaffCount', 'visitorsCount']) {
        const v = r[field];
        if (typeof v === 'number' && Number.isFinite(v) && !Number.isInteger(v)) {
          r[field] = Math.trunc(v);
        }
      }
    }
  }

  /** Display value for Students input (empty when null/0). */
  formatStudentCountInput(row: any): string {
    const n = Number(row?.studentCount ?? NaN);
    if (!Number.isFinite(n) || n <= 0) {
      return '';
    }
    return String(Math.trunc(n));
  }

  /** Limit typed digits to Student Strength length (still capped by value check). */
  studentCountMaxLength(row: any): number {
    const max = Number(row?.scaitsStudentCount ?? NaN);
    if (Number.isFinite(max) && max >= 0) {
      return Math.max(1, String(Math.trunc(max)).length);
    }
    return 6;
  }

  /** Block non-digits; Students cannot exceed Student Strength in the input. */
  onStudentCountInput(row: any, event: Event): void {
    if (!row || this.readOnly) {
      return;
    }
    const input = event.target as HTMLInputElement;
    const digitsOnly = String(input.value ?? '').replace(/\D/g, '');
    if (!digitsOnly) {
      row.studentCount = null;
      input.value = '';
      return;
    }

    let value = Math.trunc(Number(digitsOnly));
    if (!Number.isFinite(value) || value < 0) {
      input.value = this.formatStudentCountInput(row);
      return;
    }

    const max = Number(row.scaitsStudentCount ?? NaN);
    if (Number.isFinite(max) && max >= 0 && value > max) {
      this.showStudentStrengthExceededAlert(max);
      value = max;
    }

    row.studentCount = value === 0 ? null : value;
    input.value = value === 0 ? '' : String(value);
  }

  onStudentCountBlur(row: any, event: Event): void {
    if (!row || this.readOnly) {
      return;
    }
    const input = event.target as HTMLInputElement;
    const max = Number(row.scaitsStudentCount ?? NaN);
    let value = Number(row.studentCount ?? NaN);

    if (!Number.isFinite(value) || value <= 0) {
      row.studentCount = null;
      input.value = '';
      return;
    }

    value = Math.trunc(value);
    if (Number.isFinite(max) && max >= 0 && value > max) {
      this.showStudentStrengthExceededAlert(max);
      value = max;
    }

    row.studentCount = value === 0 ? null : value;
    input.value = value === 0 ? '' : String(value);
  }

  onStudentCountKeydown(event: KeyboardEvent, row: any): void {
    if (event.key === '.') {
      event.preventDefault();
      return;
    }
    // Allow control / navigation keys.
    if (
      event.ctrlKey ||
      event.metaKey ||
      event.altKey ||
      [
        'Backspace',
        'Delete',
        'Tab',
        'Escape',
        'Enter',
        'ArrowLeft',
        'ArrowRight',
        'ArrowUp',
        'ArrowDown',
        'Home',
        'End',
      ].includes(event.key)
    ) {
      return;
    }
    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
      return;
    }

    // Block keystrokes that would push the value above Student Strength.
    const max = Number(row?.scaitsStudentCount ?? NaN);
    if (!Number.isFinite(max) || max < 0) {
      return;
    }
    const input = event.target as HTMLInputElement;
    const start = input.selectionStart ?? input.value.length;
    const end = input.selectionEnd ?? input.value.length;
    const next = `${input.value.slice(0, start)}${event.key}${input.value.slice(end)}`.replace(/\D/g, '');
    if (!next) {
      return;
    }
    const nextN = Number(next);
    if (Number.isFinite(nextN) && nextN > max) {
      event.preventDefault();
      this.showStudentStrengthExceededAlert(max);
      row.studentCount = max === 0 ? null : max;
      input.value = max === 0 ? '' : String(max);
    }
  }

  /** Alert when Students exceed building Student Strength. */
  private showStudentStrengthExceededAlert(max: number): void {
    if (this.studentStrengthAlertOpen) {
      return;
    }
    this.studentStrengthAlertOpen = true;
    const ref = this.matDialog.open(ConfirmationMessageDialogComponent, {
      width: '400px',
      maxWidth: '92vw',
      autoFocus: false,
      restoreFocus: true,
      data: {
        title: 'Student Count Exceeded',
        message: `Student count cannot be more than Student Strength (${this.inrGroupedNumber(max)}).`,
        confirmLabel: 'OK',
        confirmResult: 'ok',
        singleAction: true,
      },
    });
    ref.afterClosed().subscribe(() => {
      this.studentStrengthAlertOpen = false;
    });
  }

  /** Block "." so counts stay whole numbers. */
  blockDecimalPoint(event: KeyboardEvent): void {
    if (event.key === '.') {
      event.preventDefault();
    }
  }

  close(): void {
    this.dialogRef.close();
  }

  onExportExcelClick(): void {
    if (!this.BuildingDetails?.length) {
      this.toast.showError('No data to export.');
      return;
    }
    const confirmRef = this.matDialog.open(ConfirmationMessageDialogComponent, {
      width: '400px',
      maxWidth: '92vw',
      autoFocus: false,
      restoreFocus: true,
      data: {
        title: 'Export Excel',
        message: 'Do you want to download the Supply Building Excel file?',
        confirmLabel: 'OK',
        confirmResult: 'ok',
      },
    });
    confirmRef.afterClosed().subscribe((result) => {
      if (result === 'ok') {
        this.exportSupplyBuildingExcel();
      }
    });
  }

  private exportSupplyBuildingExcel(): void {
    const rows = this.BuildingDetails ?? [];
    if (!rows.length) {
      this.toast.showError('No data to export.');
      return;
    }

    const workbook = new Workbook();
    const ws = workbook.addWorksheet('Supply Building');
    const headers = [
      'Buildings',
      'Scaits student strength',
      'Student',
      'Employee',
      'Substaff',
      'Other',
    ];
    const headerRow = ws.addRow(headers);
    headerRow.font = { bold: true };
    headerRow.eachCell((cell) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFF2F2F2' },
      };
      cell.border = {
        top: { style: 'thin' },
        bottom: { style: 'thin' },
        left: { style: 'thin' },
        right: { style: 'thin' },
      };
      cell.alignment = { vertical: 'middle', horizontal: 'left' };
    });

    const n = (value: any) => {
      const parsed = Number(value ?? 0);
      return Number.isFinite(parsed) ? parsed : 0;
    };

    rows.forEach((row) => {
      const dataRow = ws.addRow([
        String(row?.supplyBuildingName ?? '').trim(),
        n(row?.scaitsStudentCount),
        n(row?.studentCount),
        n(row?.employeeCount),
        n(row?.subStaffCount),
        n(row?.visitorsCount),
      ]);
      dataRow.eachCell((cell, colNumber) => {
        cell.border = {
          top: { style: 'thin' },
          bottom: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' },
        };
        cell.alignment = {
          vertical: 'middle',
          horizontal: colNumber === 1 ? 'left' : 'right',
        };
      });
    });

    ws.columns = [
      { width: 40 },
      { width: 24 },
      { width: 14 },
      { width: 14 },
      { width: 14 },
      { width: 14 },
    ];
    ws.autoFilter = {
      from: { row: 1, column: 1 },
      to: { row: 1, column: headers.length },
    };
    ws.views = [{ state: 'frozen', ySplit: 1 }];

    workbook.xlsx.writeBuffer().then((buffer) => {
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const datePart = String(this.currentDate ?? '').replace(/\s+/g, '_') || 'export';
      fs.saveAs(blob, `SupplyBuilding_${datePart}.xlsx`);
    }).catch(() => {
      this.toast.showError('Excel export failed.');
    });
  }

  submit(): void {
    // Require at least one non-zero count across all buildings before submit.
    if (Number(this.totalHeadCount ?? 0) <= 0) {
      this.toast.showError('Please enter Student / Employee / SubStaff / Visitors atleast one .');
      return;
    }
    const over = (this.BuildingDetails ?? []).find((r: any) => {
      const max = Number(r?.scaitsStudentCount ?? NaN);
      const students = Number(r?.studentCount ?? 0);
      return Number.isFinite(max) && max >= 0 && students > max;
    });
    if (over) {
      const max = Number(over.scaitsStudentCount);
      this.showStudentStrengthExceededAlert(max);
      over.studentCount = max === 0 ? null : max;
      return;
    }
    this.dialogRef.close({ supplyBuildings: this.BuildingDetails });
  }

  private normalizeRow(r: any): any {
    const scaitsN = Number(r?.scaitsStudentCount ?? NaN);
    return {
      ...r,
      scaitsStudentCount:
        Number.isFinite(scaitsN) && scaitsN >= 0 ? scaitsN : null,
      studentCount: this.toDisplayCount(r.studentCount),
      employeeCount: this.toDisplayCount(r.employeeCount),
      subStaffCount: this.toDisplayCount(r.subStaffCount),
      visitorsCount: this.toDisplayCount(r.visitorsCount),
    };
  }

  /** Keep 0/empty as null so inputs stay empty and show placeholder "0". */
  private toDisplayCount(value: any): number | null {
    const n = Number(value ?? 0);
    if (!Number.isFinite(n) || n === 0) {
      return null;
    }
    return n;
  }
}
